import {
    Box,
    Flex,
    Switch,
    Text,
    Tr,
    VStack,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import type {
    AlwaysPreferOemOverride,
    OemOnlyBelowPriceThreshold,
    OlderVehiclePreference,
} from '../../../../hooks/useGetSettings';
import {
    SettingsAddItemsModal,
    SettingsCard,
    SettingsNumberField,
    SettingsRow,
    SettingsSubTable,
    SettingsTd,
    SettingsTh,
    useSubTablePagination,
} from '../../../../components/settings/primitives';
import { Button, Icon, IconButton } from '../../../../ui';
import {
    DEFAULT_OEM_ONLY_BELOW_PRICE_THRESHOLD,
    DEFAULT_OLDER_VEHICLE_PREFERENCE,
    useSelectionDraft,
} from './shared';
import type { SelectionSectionProps } from './types';

interface ShopLocation {
    _id: string;
    title: string;
}

interface PartTypesSectionProps extends SelectionSectionProps {
    locations: Array<ShopLocation>;
}

const locationLabel = (override: AlwaysPreferOemOverride): string =>
    override.locations === 'all'
        ? 'All locations'
        : override.locations.map((location) => location.title).join(', ');

const coveredLocationIds = (overrides: Array<AlwaysPreferOemOverride>, locationIds: Array<string>): Set<string> => {
    const covered = new Set<string>();
    overrides.forEach((override) => {
        if (override.locations === 'all') {
            locationIds.forEach((id) => covered.add(id));
            return;
        }
        override.locations.forEach((location) => covered.add(location._id));
    });
    return covered;
};

const PartTypesSection = ({ data, locations }: PartTypesSectionProps) => {
    const { selection, updateSelection } = useSelectionDraft(data);

    const olderVehiclePreference = selection.olderVehiclePreference ?? DEFAULT_OLDER_VEHICLE_PREFERENCE;
    const oemOnlyBelowPriceThreshold = selection.oemOnlyBelowPriceThreshold ?? DEFAULT_OEM_ONLY_BELOW_PRICE_THRESHOLD;

    const handleUpdateOlderVehiclePreference = (value: Partial<OlderVehiclePreference>) => {
        updateSelection({ olderVehiclePreference: { ...olderVehiclePreference, ...value } });
    };

    const handleUpdateOemOnlyBelowPriceThreshold = (value: Partial<OemOnlyBelowPriceThreshold>) => {
        updateSelection({ oemOnlyBelowPriceThreshold: { ...oemOnlyBelowPriceThreshold, ...value } });
    };

    const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
    const [locationSearch, setLocationSearch] = useState('');
    const [pendingLocationIds, setPendingLocationIds] = useState<Set<string>>(new Set());

    const oemOverrides = useMemo(
        () => selection.alwaysPreferOemOverrides ?? [],
        [selection.alwaysPreferOemOverrides],
    );
    const shopLocations = useMemo(
        () => locations.filter((location) => location._id !== 'all'),
        [locations],
    );
    const overrideRows = useMemo(
        () => oemOverrides
            .map((entry, index) => ({ entry, index }))
            .sort((a, b) => locationLabel(a.entry).localeCompare(locationLabel(b.entry))),
        [oemOverrides],
    );
    const overridePaging = useSubTablePagination(overrideRows);
    const availableLocations = useMemo(() => {
        const taken = coveredLocationIds(oemOverrides, shopLocations.map((location) => location._id));
        return shopLocations
            .filter((location) => !taken.has(location._id))
            .sort((a, b) => a.title.localeCompare(b.title));
    }, [oemOverrides, shopLocations]);
    const filteredLocations = useMemo(() => {
        const search = locationSearch.trim().toLowerCase();
        if (!search) return availableLocations;
        return availableLocations.filter((location) => location.title.toLowerCase().includes(search));
    }, [availableLocations, locationSearch]);

    const openLocationModal = () => {
        setLocationSearch('');
        setPendingLocationIds(new Set());
        setIsLocationModalOpen(true);
    };

    const togglePendingLocation = (locationId: string) => {
        setPendingLocationIds((prev) => {
            const next = new Set(prev);
            if (next.has(locationId)) next.delete(locationId);
            else next.add(locationId);
            return next;
        });
    };

    const writeOverrides = (next: Array<AlwaysPreferOemOverride>) => {
        updateSelection({ alwaysPreferOemOverrides: next });
    };

    const handleAddOverrides = () => {
        if (pendingLocationIds.size === 0) return;
        const locations = shopLocations
            .filter((location) => pendingLocationIds.has(location._id))
            .map((location) => ({ _id: location._id, title: location.title }));
        writeOverrides([
            ...oemOverrides,
            { locations, maxMargin: 0, minPrice: 0 },
        ]);
        setIsLocationModalOpen(false);
    };

    const handleOverrideChange = (index: number, patch: Partial<Pick<AlwaysPreferOemOverride, 'maxMargin' | 'minPrice'>>) => {
        writeOverrides(oemOverrides.map((entry, current) => (
            current === index ? { ...entry, ...patch } : entry
        )));
    };

    const handleRemoveOverride = (index: number) => {
        writeOverrides(oemOverrides.filter((_, current) => current !== index));
    };

    return (
        <VStack spacing={6} align="stretch" pb={16}>
            <SettingsCard
                title="OEM preference"
                description="Configure how the AI handles OEM parts selection."
            >
                <SettingsRow
                    label="OEM parts are required"
                    description="OEM parts will be selected over all other part types unless specified otherwise in your allowed parts list, even when considering margin and price matching."
                    control={
                        <Switch
                            size="lg"
                            colorScheme="partbay-blue"
                            isChecked={selection.alwaysPreferOem}
                            onChange={(e) => updateSelection({ alwaysPreferOem: e.target.checked })}
                        />
                    }
                >
                    {/* What counts as OEM only matters once OEM is preferred,
                        so it sits inside that setting rather than beside it. */}
                    {selection.alwaysPreferOem && (
                        <Box pl={6} borderLeft="2px solid" borderColor="gray.200">
                            <Flex justify="space-between" align="center" gap={6}>
                                <Box minW={0}>
                                    <Text fontSize="sm" fontWeight="medium" color="gray.800">
                                        Treat Re-Man / Re-Con / Recycled as OEM
                                    </Text>
                                    <Text fontSize="sm" color="gray.500" mt={0.5}>
                                        When enabled, we will consider Re-Man / Re-Con / Recycled
                                        parts as OEM. However, DRP rules and your forbidden part
                                        rules will still override this.
                                    </Text>
                                </Box>
                                <Switch
                                    size="lg"
                                    colorScheme="partbay-blue"
                                    isChecked={selection.treatRemanRcyAsOem}
                                    onChange={(e) => updateSelection({ treatRemanRcyAsOem: e.target.checked })}
                                />
                            </Flex>

                            {/* When the OEM-only rule stops applying is part of
                                the same setting, so it nests here too. */}
                            <Flex justify="space-between" align="center" gap={6} mt={5}>
                                <Box minW={0}>
                                    <Text fontSize="sm" fontWeight="medium" color="gray.800">
                                        Allow non-OEM parts on older vehicles
                                    </Text>
                                    <Text fontSize="sm" color="gray.500" mt={0.5}>
                                        When enabled, automated selection will stop enforcing
                                        OEM-only filtering once the repair vehicle reaches the
                                        configured age. OEM endorsements still apply.
                                    </Text>
                                </Box>
                                <Switch
                                    size="lg"
                                    colorScheme="partbay-blue"
                                    isChecked={olderVehiclePreference.enabled}
                                    onChange={(e) => handleUpdateOlderVehiclePreference({ enabled: e.target.checked })}
                                />
                            </Flex>
                            {/* Neither indented nor ruled: the age is required
                                once the toggle is on, so it reads as part of
                                that setting rather than a choice of its own. */}
                            <Box mt={3}>
                                <Flex justify="space-between" align="center" gap={6}>
                                    <Text fontSize="sm" fontWeight="medium" color="gray.700">
                                        Vehicle age threshold
                                    </Text>
                                    <SettingsNumberField
                                        value={olderVehiclePreference.minVehicleAgeYears}
                                        onChange={(next) =>
                                            handleUpdateOlderVehiclePreference({ minVehicleAgeYears: next })
                                        }
                                        allowEmpty={false}
                                        min={1}
                                        max={30}
                                        width="80px"
                                        unit="years"
                                        ariaLabel="Vehicle age threshold"
                                        isDisabled={!olderVehiclePreference.enabled}
                                    />
                                </Flex>
                            </Box>
                        </Box>
                    )}
                </SettingsRow>

                {selection.alwaysPreferOem && (
                    <SettingsRow
                        label="Custom overrides"
                        description="At these locations, non-OEM options are acceptable when every OEM option is below the max margin and above the min price."
                        control={
                            <Button
                                variant="solid"
                                colorScheme="brand"
                                size="sm"
                                onClick={openLocationModal}
                                isDisabled={availableLocations.length === 0}
                            >
                                Add Override
                            </Button>
                        }
                    >
                        <SettingsSubTable
                            isInset
                            currentPage={overridePaging.currentPage}
                            totalPages={overridePaging.totalPages}
                            onPageChange={overridePaging.setCurrentPage}
                            head={
                                <>
                                    <SettingsTh>Location</SettingsTh>
                                    <SettingsTh w="140px">Max margin</SettingsTh>
                                    <SettingsTh w="140px">Min price</SettingsTh>
                                    <SettingsTh textAlign="center" w="100px">
                                        Actions
                                    </SettingsTh>
                                </>
                            }
                        >
                            {overrideRows.length === 0 ? (
                                <Tr>
                                    <SettingsTd colSpan={4} py={6} color="gray.500">
                                        No custom overrides — OEM is preferred at every location.
                                    </SettingsTd>
                                </Tr>
                            ) : (
                                overridePaging.pageItems.map(({ entry, index }) => (
                                    <Tr key={`${index}-${locationLabel(entry)}`}>
                                        <SettingsTd fontWeight="medium" color="gray.800">
                                            {locationLabel(entry)}
                                        </SettingsTd>
                                        <SettingsTd>
                                            <SettingsNumberField
                                                value={Math.round(entry.maxMargin * 100)}
                                                onChange={(next) => handleOverrideChange(index, { maxMargin: next / 100 })}
                                                allowEmpty={false}
                                                min={0}
                                                max={100}
                                                width="88px"
                                                unit="%"
                                                ariaLabel={`Max margin for ${locationLabel(entry)}`}
                                            />
                                        </SettingsTd>
                                        <SettingsTd>
                                            <SettingsNumberField
                                                value={entry.minPrice}
                                                onChange={(next) => handleOverrideChange(index, { minPrice: next })}
                                                allowEmpty={false}
                                                allowDecimals
                                                min={0}
                                                width="96px"
                                                prefix="$"
                                                ariaLabel={`Min price for ${locationLabel(entry)}`}
                                            />
                                        </SettingsTd>
                                        <SettingsTd textAlign="center">
                                            <IconButton
                                                aria-label={`Remove ${locationLabel(entry)} override`}
                                                icon={<Icon name="trash" size="sm" />}
                                                size="sm"
                                                variant="ghost"
                                                colorScheme="red"
                                                onClick={() => handleRemoveOverride(index)}
                                            />
                                        </SettingsTd>
                                    </Tr>
                                ))
                            )}
                        </SettingsSubTable>
                    </SettingsRow>
                )}

                {!selection.alwaysPreferOem ? (
                    <SettingsRow
                        label="Prefer OEM below a dollar threshold"
                        description="When enabled, if the OEM buy price is below this tresshold, OEM options will be preferred over non-OEM options."
                        control={
                            <Switch
                                size="lg"
                                colorScheme="partbay-blue"
                                isChecked={oemOnlyBelowPriceThreshold.enabled}
                                onChange={(e) => handleUpdateOemOnlyBelowPriceThreshold({ enabled: e.target.checked })}
                            />
                        }
                    >
                        <Box pl={6} borderLeft="2px solid" borderColor="gray.200">
                            <Flex justify="space-between" align="center" gap={6}>
                                <Text fontSize="sm" fontWeight="medium" color="gray.700">
                                    OEM buy price threshold
                                </Text>
                                <SettingsNumberField
                                    value={oemOnlyBelowPriceThreshold.maxBuyPrice}
                                    onChange={(next) =>
                                        handleUpdateOemOnlyBelowPriceThreshold({ maxBuyPrice: next })
                                    }
                                    allowEmpty={false}
                                    min={1}
                                    max={10000}
                                    width="100px"
                                    prefix="$"
                                    ariaLabel="OEM buy price threshold"
                                    isDisabled={!oemOnlyBelowPriceThreshold.enabled}
                                />
                            </Flex>
                        </Box>
                    </SettingsRow>
                ) : null}

                {!selection.alwaysPreferOem ? (
                    <SettingsRow
                        label="CAPA certified A/M parts are preferred over non-certified"
                        description="CAPA certified A/M parts will rank higher than all non-certified A/M parts, even when considering margin and price matching."
                        control={
                            <Switch
                                size="lg"
                                colorScheme="partbay-blue"
                                isChecked={selection.alwaysPreferCapa}
                                onChange={(e) => updateSelection({ alwaysPreferCapa: e.target.checked })}
                            />
                        }
                    />
                ) : null}
            </SettingsCard>

            <SettingsCard
                title="Recycled parts and wheels"
                description="Configure recycled parts criteria and wheel handling."
            >
                <SettingsRow
                    label="Recycled source vehicle restriction"
                    description="Requires the source vehicle's mileage to be less than or equal to the repair vehicle's mileage and the source vehicle's year is at least as recent as the repair vehicle's year."
                    control={
                        <Switch
                            size="lg"
                            colorScheme="partbay-blue"
                            isChecked={selection.rcySourceVehicleRestriction}
                            onChange={(e) => updateSelection({ rcySourceVehicleRestriction: e.target.checked })}
                        />
                    }
                />

                <SettingsRow
                    label="Flag wheels"
                    description="Automatically flag wheels so you can check pricing and decide if a sublet is more cost effective."
                    control={
                        <Switch
                            size="lg"
                            colorScheme="partbay-blue"
                            isChecked={selection.flagWheels}
                            onChange={(e) => updateSelection({ flagWheels: e.target.checked })}
                        />
                    }
                />

                <SettingsRow
                    label="Flag Price Match to LKQ"
                    description="Automatically flag when price is matched to LKQ option."
                    control={
                        <Switch
                            size="lg"
                            colorScheme="blue"
                            isChecked={selection.flagLkqPriceMatched ?? false}
                            onChange={(e) => updateSelection({ flagLkqPriceMatched: e.target.checked })}
                        />
                    }
                />

                <SettingsRow
                    label="Flag LKQ when Selected"
                    description="Automatically flag when an LKQ part is the selected option."
                    control={
                        <Switch
                            size="lg"
                            colorScheme="blue"
                            isChecked={selection.flagLkqSelected ?? false}
                            onChange={(e) => updateSelection({ flagLkqSelected: e.target.checked })}
                        />
                    }
                />
            </SettingsCard>

            <SettingsAddItemsModal
                isOpen={isLocationModalOpen}
                onClose={() => setIsLocationModalOpen(false)}
                modalTitle="Add OEM preference override"
                searchPlaceholder="Search locations..."
                searchTerm={locationSearch}
                onSearchTermChange={setLocationSearch}
                emptySearchLabel={locationSearch ? 'No locations found' : 'All locations already overridden'}
                items={filteredLocations.map((location) => ({
                    id: location._id,
                    label: location.title,
                }))}
                pendingIds={pendingLocationIds}
                onTogglePending={togglePendingLocation}
                selectedCountLabel={
                    pendingLocationIds.size > 0
                        ? `${pendingLocationIds.size} location${pendingLocationIds.size === 1 ? '' : 's'} selected`
                        : null
                }
                onAdd={handleAddOverrides}
            />
        </VStack>
    );
};

export default PartTypesSection;
